import CircleLine from "@/components/common/log/circle-line";
import type { LastedSignedDocument } from "@/queries/process/signature.api";
import { cn } from "@/utils/theme";
import { RowCard } from "@elaniintc/goes-design-system";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Icon,
} from "@elaniintc/goes-design-system";
import dayjs from "dayjs";

interface SignDocumentLogsProps {
  title: string;
  items: LastedSignedDocument[];
  emptyText?: string;
}

const SignDocumentTimeline = ({
  title,
  items,
  emptyText = "No se encontraron resultados",
}: SignDocumentLogsProps) => {
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "-";
    return dayjs(date).format("MMM D, YYYY | hh:mm A");
  };

  return (
    <div className="flex flex-col w-full h-full leading-7 text-body font-normal text-base">
      {items.length > 0 && <h2 className="mb-6">{title}</h2>}

      {items.length !== 0 && (
        <div className="relative">
          {items.map((log, index) => {
            const isLast = index === items.length - 1;
            const isSuccess = log.status === "signed";
            return (
              <div
                key={log.id}
                className={cn("flex gap-3 -mt-2", !isLast && "mb-0")}
              >
                <CircleLine
                  option={index === 0 ? "init" : isLast ? "end" : "middle"}
                  showLine={!isLast}
                />

                <div className={cn("flex-1", !isLast && "pb-6")}>
                  <div className="flex space-x-2 items-center pb-2">
                    <span className="font-semibold text-body">
                      Firma de documento{" "}
                      {log.file?.file_name ??
                        log.file?.filename ??
                        "Sin nombre"}
                    </span>
                  </div>

                  <div className="w-full mb-2 bg-neutral-object flex items-center gap-4 border border-neutral rounded-xl p-3 shadow-neutral-xs">
                    <RowCard.Left>
                      <span>Firma de documento</span>
                      <Badge>
                        <span>
                          {log.file?.file_name ??
                            log.file?.filename ??
                            "Sin nombre"}{" "}
                          <Icon type="fa" name="file" size="xs" />
                        </span>
                      </Badge>
                      <span>fue</span>
                      <Badge variant={isSuccess ? "success" : "danger"}>
                        <span>
                          {isSuccess ? "Exitosa" : "Fallida"}{" "}
                          <Icon
                            type="fa"
                            name={isSuccess ? "check" : "x"}
                            size="xs"
                          />
                        </span>
                      </Badge>
                    </RowCard.Left>
                  </div>

                  <div className="w-full">
                    <div className="flex items-center">
                      <span className="text-body">Por</span>
                      <Avatar className="mx-2">
                        <AvatarFallback color="action">
                          {log?.signer?.name?.charAt(0).toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-action">
                        {log.signer?.name
                          ? `${log.signer?.name} ${log.signer?.last_name ?? ""}`.trim()
                          : "Desconocido"}
                      </span>
                    </div>
                    <span className="ml-auto pr-2">
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <br />
        </div>
      )}

      {items.length === 0 && (
        <div className="flex-1 text-center text-body-quiet flex items-center justify-center ">
          {emptyText}
        </div>
      )}
    </div>
  );
};

export default SignDocumentTimeline;
